import { PutUtilityContract, WaterContract } from "@/api/facilities";
import ImpactPill from "@/components/Impact/ImpactPill";
import RootModal from "@/components/RootModal/RootModal";
import useAnalytics from "@/hooks/useAnalytics";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import {
  useGetFacility,
  useGetFacilityProductMass,
  useGetWaterOptions,
  useInsertContract,
  useUpdateContract,
} from "@/hooks/useFacilities";
import useTranslation from "@/hooks/useTranslation";
import usePublishedId from "@/pages/facilities/hooks/usePublishedId";
import { unitMapping } from "@/utilities/units";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { Col, DatePicker, Form, InputNumber, message, Row, Segmented, Space, Tooltip } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { useEffect } from "react";
import { multiplyImpactWithFactor } from "../../utils";
import EstimationSwitch from "../EstimationSwitch";
import styles from "../Modal.module.scss";

const { RangePicker } = DatePicker;

interface WaterContractForm {
  dateRange: [dayjs.Dayjs, dayjs.Dayjs];
  unit: string;
  consumption: number;
  estimation: boolean;
  percentage: number;
}

export default function WaterContractModal({
  facilityId,
  open,
  closeModal,
  editContract: contract,
  minDate,
  maxDate,
}: {
  facilityId: number;
  open: boolean;
  closeModal: () => void;
  editContract?: WaterContract;
  minDate: dayjs.Dayjs;
  maxDate: dayjs.Dayjs;
}) {
  const publishedId = usePublishedId();
  const translation = useTranslation();
  const postContract = useInsertContract();
  const putContract = useUpdateContract();
  const [form] = useForm<WaterContractForm>();
  const analytics = useAnalytics({ enabled: !publishedId });
  const { data, isLoading } = useGetWaterOptions(facilityId, open);

  const unit = useWatch("unit", form);
  const amount = useWatch("consumption", form);
  const percentage = (useWatch("percentage", form) || 0) / 100;
  const total = Math.round(amount * percentage * 100) / 100;

  const selectedDataset = data?.find((x) => x.unit === unit);

  const estimation = useWatch("estimation", form);
  const dateRange = useWatch("dateRange", form);
  const { data: productMassData } = useGetFacilityProductMass({
    facilityId,
    startDate: dateRange ? dateRange[0] : minDate,
    endDate: dateRange ? dateRange[1] : maxDate,
  });

  let productMass = productMassData?.product_mass;

  const facility = useGetFacility(facilityId);
  const company = useCompanySettings();

  const handleError = (error: AxiosError<{ message: string }>) => {
    if (error?.response?.data?.message === "WATER_ADDRESS_ERROR") {
      message.error(
        "The address of this facility could not be matched to a geography to calculate water impact. Please edit the address and try again."
      );
    } else {
      message.error(translation.errors.general);
    }
  };

  useEffect(() => {
    if (contract && contract.sources[0]) {
      form.setFieldsValue({
        dateRange: [dayjs(contract.start_date), dayjs(contract.end_date)],
        unit: contract.sources[0].unit,
        consumption: contract.sources[0].amount || undefined,
        estimation: contract.contract_type === "estimate",
        percentage: contract.sources[0].percentage * 100 || 1,
      });
    } else {
      form.setFieldsValue({
        dateRange: [minDate, maxDate],
      });
    }
  }, [contract, minDate, maxDate]);

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (!selectedDataset) return;

        const contractToPutPost: PutUtilityContract = {
          facility_id: facilityId,
          end_date: values.dateRange[1].format("YYYY-MM-01"),
          start_date: values.dateRange[0].format("YYYY-MM-01"),
          type: "water",
          contract_type: values.estimation ? "estimate" : "actual",
          sources: [
            {
              urn: "WATER",
              amount: values.consumption,
              unit: values.unit,
              capital_owned: false,
              percentage: values.estimation ? 1 : values.percentage / 100,
            },
          ],
        };

        if (contract) {
          putContract
            .mutateAsync({
              contractId: contract.id,
              contract: { id: contract.id, ...contractToPutPost },
            })
            .then((result) => {
              if (result.status === 200) {
                analytics.track("Contract Updated");
                message.success("Water contract updated.");
                closeModal();
              }
            })
            .catch((error) => handleError(error));
        } else {
          postContract
            .mutateAsync({
              contract: contractToPutPost,
            })
            .then((result) => {
              if (result.status === 200) {
                analytics.track("Contract Added");
                message.success("Water contract added.");
                closeModal();
              }
            })
            .catch((error) => handleError(error));
        }
      })
      .catch(() => {
        message.error("Contract could not be added. Please check for missing or incorrect fields.");
      });
  };

  return (
    <RootModal
      className={[styles.ContractModal, styles.heat].join(" ")}
      open={open}
      width={1000}
      onCancel={() => closeModal()}
      okText={"Save"}
      keyboard={false}
      okButtonProps={{
        loading: postContract.isLoading || putContract.isLoading,
      }}
      onOk={onSubmit}
      title={"Add water contract"}
    >
      <Form
        form={form}
        onKeyUp={(e) => {
          if (e.code === "Enter") onSubmit();
        }}
        initialValues={{
          unit: "l",
          percentage: 100,
        }}
      >
        <div className={styles.steps}>
          <div className={styles.step}>
            <div className={styles["step-heading"]}>1. Select timeframe</div>
            <div className={styles["step-content"]}>
              <div>
                <Form.Item
                  name="dateRange"
                  rules={[
                    {
                      required: true,
                      message: "Date range is required.",
                    },
                  ]}
                >
                  <RangePicker picker="month" />
                </Form.Item>
              </div>
            </div>
          </div>
          <div className={styles.step}>
            <div className={styles["step-heading"]}>2. Define water consumption</div>
            <div className={styles["step-subtext"]}>
              This information can be requested from your water supplier or your own managed supply.
            </div>
            <div className={styles["step-content"]}>
              <div style={{ width: "100%" }}>
                <div className={styles.sources}>
                  <div className={styles.sourcesWrapper}>
                    <div className={styles["heat-details"]}>
                      <div className={styles.title}>Tap water</div>
                      <Row
                        gutter={[8, 12]}
                        align="middle"
                      >
                        {estimation ? (
                          <>
                            <Col span={10}>
                              <Space>
                                Consumption estimate per kg
                                <Tooltip title={translation.utilities.consumptionEstimate}>
                                  <QuestionCircleOutlined />
                                </Tooltip>
                              </Space>
                            </Col>
                            <Col span={10}>
                              <Form.Item
                                noStyle
                                name="consumption"
                                rules={[
                                  {
                                    required: true,
                                  },
                                ]}
                              >
                                <InputNumber
                                  controls={false}
                                  style={{ width: "100%" }}
                                  addonAfter={unit}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={4}>
                              <Form.Item
                                noStyle
                                name="unit"
                                rules={[
                                  {
                                    required: true,
                                  },
                                ]}
                              >
                                <Segmented
                                  options={(data || []).map((x) => ({
                                    label: unitMapping?.[x.unit] || x.unit,
                                    value: x.unit,
                                  }))}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={10}>
                              <Space>
                                Total product mass in facility
                                <Tooltip title={translation.utilities.totalProductMassInFacility}>
                                  <QuestionCircleOutlined />
                                </Tooltip>
                              </Space>
                            </Col>
                            <Col span={10}>
                              <InputNumber
                                addonAfter={"kg"}
                                disabled
                                style={{ width: "100%" }}
                                value={productMass}
                              />
                            </Col>
                            <Col span={4}></Col>
                            <Col span={10}>
                              <Space>
                                Total consumption estimate
                                <Tooltip title={translation.utilities.totalConsumptionEstimate}>
                                  <QuestionCircleOutlined />
                                </Tooltip>
                              </Space>
                            </Col>
                            <Col span={10}>
                              <InputNumber
                                addonAfter={unitMapping[unit]}
                                disabled
                                style={{ width: "100%" }}
                                value={productMass ? productMass * amount : 0}
                              />
                            </Col>
                            <Col span={4}></Col>
                          </>
                        ) : (
                          <>
                            <Col span={10}>Total consumed at {facility.data?.facility_name}</Col>
                            <Col span={10}>
                              <Form.Item
                                noStyle
                                name="consumption"
                                rules={[
                                  {
                                    required: true,
                                  },
                                ]}
                              >
                                <InputNumber
                                  controls={false}
                                  style={{ width: "100%" }}
                                  addonAfter={unit}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={4}>
                              <Form.Item
                                noStyle
                                name="unit"
                                rules={[
                                  {
                                    required: true,
                                  },
                                ]}
                              >
                                <Segmented
                                  options={(data || []).map((x) => ({
                                    label: unitMapping?.[x.unit] || x.unit,
                                    value: x.unit,
                                  }))}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={10}>Percentage for {company.data?.company_name}</Col>
                            <Col span={14}>
                              <Form.Item
                                noStyle
                                name="percentage"
                                rules={[
                                  {
                                    required: true,
                                  },
                                ]}
                              >
                                <InputNumber
                                  controls={false}
                                  min={0}
                                  max={100}
                                  style={{ width: "100%" }}
                                  addonAfter={"%"}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={10}>Total consumed for {company.data?.company_name}</Col>
                            <Col span={14}>
                              <InputNumber
                                value={total}
                                controls={false}
                                style={{ width: "100%" }}
                                addonAfter={unit}
                                disabled
                              />
                            </Col>
                          </>
                        )}
                        <Col span={10}></Col>
                        <Col span={10}>
                          <ImpactPill
                            impact={multiplyImpactWithFactor(
                              selectedDataset?.impact,
                              estimation ? (productMass || 0) * amount : total
                            )}
                            loading={isLoading}
                          />
                        </Col>
                      </Row>
                    </div>
                  </div>
                  <EstimationSwitch />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </RootModal>
  );
}
